/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-11-17 16:58:53
 *
 *******************************************************/
// @flow

import React, { Component } from 'react';
import { Icon, Card, Spinner, Tooltip } from '@shopify/polaris';
import { CaretUpMinor, CaretDownMinor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import Chart from 'react-apexcharts';
import classnames from 'classnames';
import { getDatasetLineOptionsV2 } from 'src/constants/analytics';
import {
  getMaxBoundaryOfChart,
  getMaxBoundaryOfChartWithLimit
} from 'src/helpers/analytics-helpers';

type Props = {
  type: string,
  title?: string,
  series: Array<Object>,
  categories: string[] | number[],
  total?: string,
  percentGrowthChange?: number,
  transparent: boolean,
  height?: string,
  inverse?: boolean,
  loading?: boolean,
  unlimitedYAxis?: boolean,
  suffixOfValue?: string,
  tooltip?: string,
  moneyFormat?: string,
  yAxisTickAmount?: number
};
type ComposedProps = Props & WithI18nProps;
// DONE
class AnalyticsApexChart extends Component<ComposedProps> {
  render() {
    const {
      title,
      series,
      total,
      percentGrowthChange,
      transparent,
      inverse,
      height = 'auto',
      loading,
      tooltip,
      className = ''
    } = this.props;
    const isCompare = series.length > 1;
    const options = this.buildOptions();

    const chartClass = classnames('BC-chart', {
      'BC-chart--transparent': transparent,
      [className]: true
    });

    const percentGrowthClass = classnames(
      'BC-chart__percent-growth',
      {
        'BC-chart__percent-growth--up':
          (!inverse && percentGrowthChange > 0) || (inverse && percentGrowthChange < 0)
      },
      {
        'BC-chart__percent-growth--down':
          (!inverse && percentGrowthChange < 0) || (inverse && percentGrowthChange > 0)
      }
    );

    const caretIcon =
      percentGrowthChange > 0 ? CaretUpMinor : percentGrowthChange < 0 ? CaretDownMinor : null;

    return (
      <div className={chartClass}>
        <Card
          sectioned
          title={
            <Tooltip content={tooltip} preferredPosition="above">
              <h2 className="Polaris-Heading">{title}</h2>
            </Tooltip>
          }>
          {loading ? (
            <div className="BC-chart-loading">
              <Spinner size="large" />
            </div>
          ) : (
            <>
              {(!!total || !!percentGrowthChange) && (
                <div className="BC-chart__overall">
                  {!!total && <p className="BC-chart__total">{total}</p>}
                  {
                    isCompare ?
                    <>
                    {
                      !!percentGrowthChange ? (
                        <p className={percentGrowthClass}>
                          <span>{Math.abs(percentGrowthChange)}%</span>
                          <Icon source={caretIcon} />
                        </p>
                      ) : (
                        <p className="BC-chart__percent-growth" style={{ fontSize: '20px' }}>
                          -
                        </p>
                      )
                    }
                    </> : 
                    <></>
                  }
                </div>
              )}
              <Chart series={series} options={options} height={height} />
            </>
          )}
        </Card>
      </div>
    );
  }

  buildOptions = () => {
    const { type, categories, series, unlimitedYAxis, suffixOfValue = '', moneyFormat, yAxisTickAmount = 4, isLimitxAxis } = this.props;
    const isCompare = series.length > 1;
    let allData = [];
    series.forEach(item => {
      allData = [...allData, ...item.data];
    });
    const maxBoundary = unlimitedYAxis
      ? getMaxBoundaryOfChart(allData, yAxisTickAmount)
      : getMaxBoundaryOfChartWithLimit(allData, yAxisTickAmount);

    switch (type) {
      case 'line':
        return getDatasetLineOptionsV2(categories, isCompare, maxBoundary, suffixOfValue, yAxisTickAmount, moneyFormat, isLimitxAxis);
      default:
        return {};
    }
  };
}

export default withI18n()(AnalyticsApexChart);
